import { useEffect, useRef, useState } from "react";
import Video from "twilio-video";
import {
  joinCallDoctor_api,
  joinCallPatient_api,
} from "../../../api/api_calls/videoCall_apiCalls";

export function useVideoRoom({
  appointmentId,
  userType,
  appointmentData,
  toast,
}) {
  const localRef = useRef();
  const participantRefs = useRef(new Map()); // Store refs for each participant
  const audioTrackRef = useRef(null);
  const videoTrackRef = useRef(null);
  const localVideoElementRef = useRef(null);
  const remoteVideoElementsRef = useRef(new Map());

  const [room, setRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isLocalVideoAttached, setIsLocalVideoAttached] = useState(false);
  const [remoteParticipants, setRemoteParticipants] = useState(new Map());
  const [maxParticipants] = useState(4); // Maximum participants including local user
  const [videoTrackReady, setVideoTrackReady] = useState(false); // Track when video track is ready

  // Participant identification helper functions
  const parseParticipantIdentity = (identity) => {
    const parts = identity.split("_");
    return {
      type: parts[0], // 'doctor', 'patient', 'guest1', 'guest2', etc.
      userId: parts[1],
    };
  };

  const getParticipantDisplayInfo = (identity, appointmentData) => {
    const parsed = parseParticipantIdentity(identity);

    if (parsed.type === "doctor" && appointmentData?.doctor) {
      return {
        name: `Dr. ${appointmentData.doctor.firstName} ${appointmentData.doctor.lastName}`.trim(),
        initials: `${appointmentData.doctor.firstName?.[0] || "D"}${appointmentData.doctor.lastName?.[0] || "R"}`,
        type: "doctor",
        participantId: identity,
      };
    }

    if (parsed.type === "patient" && appointmentData?.patient) {
      return {
        name: `${appointmentData.patient.firstName} ${appointmentData.patient.lastName}`.trim(),
        initials: `${appointmentData.patient.firstName?.[0] || "P"}${appointmentData.patient.lastName?.[0] || "T"}`,
        type: "patient",
        participantId: identity,
      };
    }

    // Handle guest participants
    if (parsed.type.startsWith("guest")) {
      const guestNumber = parsed.type.replace("guest", "");
      return {
        name: `Guest ${guestNumber}`,
        initials: `G${guestNumber}`,
        type: "guest",
        participantId: identity,
      };
    }

    // Fallback for unknown participant types
    return {
      name:
        parsed.type === "doctor"
          ? "Doctor"
          : parsed.type === "patient"
            ? "Patient"
            : "Participant",
      initials:
        parsed.type === "doctor"
          ? "DR"
          : parsed.type === "patient"
            ? "PT"
            : "PA",
      type: parsed.type,
      participantId: identity,
    };
  };

  // Fetch video call token from API
  const fetchVideoCallToken = async () => {
    if (!appointmentId) {
      setError("No appointment ID provided");
      toast({
        title: "Error",
        description: "No appointment ID provided",
        variant: "destructive",
      });
      setIsLoading(false);
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);

      let response;
      if (userType === "doctor") {
        response = await joinCallDoctor_api({ appointmentId });
      } else if (userType === "patient") {
        response = await joinCallPatient_api({ appointmentId });
      } else {
        throw new Error("Invalid user type");
      }

      if (response?.data?.token) {
        return response.data.token;
      } else {
        throw new Error("No token received from server");
      }
    } catch (error) {
      console.error("Failed to fetch video call token:", error);
      const errorMessage =
        error.message || "Failed to join video call. Please try again.";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsLoading(false);
      return null;
    }
  };

  // Helper function to attach remote tracks
  const attachRemoteTrack = (track, participantIdentity) => {
    if (track.kind === "video") {
      console.log("📺 Attaching remote video track from:", participantIdentity);

      // Check if we've reached the maximum number of participants
      if (remoteParticipants.size >= maxParticipants - 1) {
        console.warn(
          "⚠️ Maximum participants reached, cannot add more video tracks",
        );
        return;
      }

      const el = track.attach();
      el.style.width = "100%";
      el.style.height = "100%";
      el.style.objectFit = "cover";
      el.playsInline = true;
      el.setAttribute("data-participant-id", participantIdentity);

      // Store the video element for this specific participant
      remoteVideoElementsRef.current.set(participantIdentity, el);

      // Update the participant's video element reference
      setRemoteParticipants((prev) => {
        const newMap = new Map(prev);
        const participantData = newMap.get(participantIdentity);
        if (participantData) {
          participantData.videoElement = el;
          newMap.set(participantIdentity, participantData);
        }
        return newMap;
      });

      console.log(
        `📊 Total remote participants: ${remoteParticipants.size + 1}`,
      );
    } else if (track.kind === "audio") {
      console.log("🔊 Attaching remote audio track from:", participantIdentity);
      const el = track.attach();
      document.body.appendChild(el); // Audio doesn't need to be visible
    }
  };

  // Helper function to detach remote tracks
  const detachRemoteTrack = (track, participantIdentity) => {
    console.log("🗑️ Detaching track from:", participantIdentity);
    track.detach().forEach((element) => {
      element.remove();
    });

    // Remove from our tracking map
    if (track.kind === "video") {
      remoteVideoElementsRef.current.delete(participantIdentity);
    }
  };

  const joinRoom = async (authToken) => {
    try {
      const roomName = `appointment_${appointmentId}`;

      console.log(
        "🎥 Starting to join room with token:",
        authToken.substring(0, 20) + "...",
      );
      console.log("🏠 Room name:", roomName);

      // Create local tracks
      console.log("📹 Creating local tracks...");
      const localTracks = await Video.createLocalTracks({
        video: true,
        audio: true,
      });

      console.log("📹 Local tracks created:", localTracks.length);
      audioTrackRef.current = localTracks.find((t) => t.kind === "audio");
      videoTrackRef.current = localTracks.find((t) => t.kind === "video");

      // Set video track ready to trigger local video attachment
      setVideoTrackReady(true);
      console.log("🎥 Video track ready, will trigger attachment");

      // Connect to Twilio room
      console.log("🔗 Connecting to Twilio room...");
      const r = await Video.connect(authToken, {
        name: roomName,
        tracks: localTracks,
      });

      console.log("✅ Connected to room:", r.name);
      console.log(
        "👤 Local participant identity:",
        r.localParticipant.identity,
      );
      console.log("👥 Existing participants:", r.participants.size);

      setRoom(r);
      setIsLoading(false);

      // Handle remote participants
      const subscribeToParticipant = (participant) => {
        console.log("👤 Subscribing to participant:", participant.identity);

        // Parse participant info for identification
        const participantInfo = getParticipantDisplayInfo(
          participant.identity,
          appointmentData,
        );
        console.log("👤 Participant info:", participantInfo);

        // Add to remote participants map
        setRemoteParticipants((prev) => {
          const newMap = new Map(prev);
          newMap.set(participant.identity, {
            participant,
            info: participantInfo,
            videoElement: null,
          });
          return newMap;
        });

        // Handle existing tracks
        participant.tracks.forEach((publication) => {
          console.log(
            "📡 Found track publication:",
            publication.kind,
            "subscribed:",
            publication.isSubscribed,
          );
          if (publication.isSubscribed && publication.track) {
            attachRemoteTrack(publication.track, participant.identity);
          }
        });

        // Handle new tracks
        participant.on("trackSubscribed", (track) => {
          console.log(
            "📡 Track subscribed:",
            track.kind,
            "from:",
            participant.identity,
          );
          attachRemoteTrack(track, participant.identity);
        });

        participant.on("trackUnsubscribed", (track) => {
          console.log(
            "📡 Track unsubscribed:",
            track.kind,
            "from:",
            participant.identity,
          );
          detachRemoteTrack(track, participant.identity);
        });
      };

      // Subscribe to existing participants
      r.participants.forEach(subscribeToParticipant);

      // Subscribe to new participants
      r.on("participantConnected", subscribeToParticipant);

      // Handle participant disconnection
      r.on("participantDisconnected", (participant) => {
        console.log("👤 Participant disconnected:", participant.identity);

        // Remove from remote participants map
        setRemoteParticipants((prev) => {
          const newMap = new Map(prev);
          const participantData = newMap.get(participant.identity);

          // Clean up video element if it exists
          if (participantData?.videoElement) {
            participantData.videoElement.remove();
          }

          newMap.delete(participant.identity);
          return newMap;
        });

        // Clean up from our video elements tracking
        const videoElement = remoteVideoElementsRef.current.get(
          participant.identity,
        );
        if (videoElement && videoElement.parentNode) {
          videoElement.remove();
        }
        remoteVideoElementsRef.current.delete(participant.identity);
      });
    } catch (err) {
      console.error("❌ joinRoom error:", err);
      setError("Failed to join video call: " + err.message);
      setIsLoading(false);
      toast({
        title: "Error",
        description: "Failed to join video call. Please try again.",
        variant: "destructive",
      });
    }
  };

  const leaveRoom = () => {
    if (room) {
      console.log("🧹 Leaving room and cleaning up...");

      // First, disconnect from the room to stop receiving remote tracks
      room.disconnect();
      console.log("📡 Disconnected from Twilio room");

      // Clean up local video element first
      if (localVideoElementRef.current) {
        localVideoElementRef.current.remove();
        localVideoElementRef.current = null;
      }

      // Stop and dispose of local tracks properly
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
        console.log("🎤 Audio track stopped");
      }

      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current = null;
        console.log("📹 Video track stopped");
      }

      // Stop all local participant tracks as backup
      room.localParticipant.tracks.forEach((publication) => {
        if (publication.track) {
          publication.track.stop();
          console.log("🛑 Stopped track:", publication.track.kind);
        }
      });

      // Clean up remote participants
      remoteParticipants.forEach((participantData) => {
        if (participantData.videoElement) {
          participantData.videoElement.remove();
        }
      });
      setRemoteParticipants(new Map());

      // Clean up remote video elements
      remoteVideoElementsRef.current.forEach((element) => {
        if (element.parentNode) {
          element.remove();
        }
      });
      remoteVideoElementsRef.current.clear();

      // Reset all states
      setIsLocalVideoAttached(false);
      setIsAudioEnabled(true);
      setIsVideoEnabled(true);
      setVideoTrackReady(false); // Reset video track ready state
      setRoom(null);

      console.log("✅ Room cleanup completed - camera should be released");
    }
  };

  const toggleAudio = () => {
    if (audioTrackRef.current) {
      const enabled = !isAudioEnabled;
      audioTrackRef.current.enable(enabled);
      setIsAudioEnabled(enabled);
    }
  };

  const toggleVideo = () => {
    if (videoTrackRef.current) {
      const enabled = !isVideoEnabled;
      videoTrackRef.current.enable(enabled);
      setIsVideoEnabled(enabled);
      console.log(`📹 Video ${enabled ? "enabled" : "disabled"}`);
    }
  };

  // useEffect to handle local video track attachment
  useEffect(() => {
    if (
      videoTrackRef.current &&
      localRef.current &&
      isVideoEnabled &&
      videoTrackReady
    ) {
      // Clean up any existing video element
      if (localVideoElementRef.current) {
        localVideoElementRef.current.remove();
        localVideoElementRef.current = null;
      }

      // Attach the video track
      const videoElement = videoTrackRef.current.attach();
      videoElement.style.width = "100%";
      videoElement.style.height = "100%";
      videoElement.style.objectFit = "cover";
      videoElement.muted = true;
      videoElement.playsInline = true;

      localRef.current.appendChild(videoElement);
      localVideoElementRef.current = videoElement;
      setIsLocalVideoAttached(true);

      console.log("✅ Local video attached via useEffect");
    } else if (
      localVideoElementRef.current &&
      (!isVideoEnabled || !videoTrackReady)
    ) {
      // Clean up video element when video is disabled or track not ready
      localVideoElementRef.current.remove();
      localVideoElementRef.current = null;
      setIsLocalVideoAttached(false);
      console.log("🧹 Local video detached via useEffect");
    }

    // Cleanup function
    return () => {
      if (localVideoElementRef.current) {
        localVideoElementRef.current.remove();
        localVideoElementRef.current = null;
        console.log("🧹 Local video element cleaned up in useEffect");
      }
    };
  }, [isVideoEnabled, videoTrackReady]); // Include videoTrackReady in dependencies

  // Note: Remote video elements are now attached directly to participant containers
  // in the component via useEffect, so no need for re-attachment logic here

  // Main initialization effect
  useEffect(() => {
    if (!appointmentId) {
      setError("No appointment ID provided");
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const initializeVideoCall = async () => {
      console.log("🚀 Initializing video call...");

      try {
        const videoToken = await fetchVideoCallToken();
        if (videoToken && isMounted) {
          console.log("✅ Token fetched, joining room...");
          await joinRoom(videoToken);
        }
      } catch (error) {
        console.error("❌ Failed to initialize video call:", error);
        if (isMounted) {
          setError("Failed to initialize video call");
          setIsLoading(false);
        }
      }
    };

    initializeVideoCall();

    // Add beforeunload listener to ensure cleanup on page close/refresh
    const handleBeforeUnload = () => {
      console.log("🚨 Page unloading - emergency cleanup");
      if (room) {
        room.disconnect();
      }
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
      }
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      console.log("🧹 Component unmounting - cleaning up video call...");
      isMounted = false;

      // Remove beforeunload listener
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Only cleanup if room still exists (avoid double cleanup)
      if (room) {
        leaveRoom();
      } else {
        // If room is already null, still clean up any remaining tracks
        if (audioTrackRef.current) {
          audioTrackRef.current.stop();
          audioTrackRef.current = null;
        }
        if (videoTrackRef.current) {
          videoTrackRef.current.stop();
          videoTrackRef.current = null;
        }
        if (localVideoElementRef.current) {
          localVideoElementRef.current.remove();
          localVideoElementRef.current = null;
        }
        // Reset states
        setVideoTrackReady(false);
        setIsLocalVideoAttached(false);
        console.log("🧹 Cleaned up remaining tracks on unmount");
      }
    };
  }, [appointmentId]);

  const retryConnection = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const videoToken = await fetchVideoCallToken();
      if (videoToken) {
        console.log("✅ Token fetched, joining room...");
        await joinRoom(videoToken);
      }
    } catch (error) {
      console.error("❌ Failed to retry video call:", error);
      setError("Failed to retry video call");
      setIsLoading(false);
    }
  };

  // Helper function to get participant layout based on total count
  const getParticipantLayout = () => {
    const totalParticipants = remoteParticipants.size + 1; // +1 for local participant

    if (totalParticipants <= 2) {
      return { layout: "side-by-side", gridCols: 2 };
    } else if (totalParticipants <= 4) {
      return { layout: "grid", gridCols: 2 };
    } else {
      return { layout: "grid", gridCols: 3 }; // Fallback for more than 4
    }
  };

  return {
    localRef,
    participantRefs,
    remoteVideoElementsRef,
    room,
    isLoading,
    error,
    isAudioEnabled,
    isVideoEnabled,
    isLocalVideoAttached,
    remoteParticipants,
    maxParticipants,
    getParticipantLayout,
    toggleAudio,
    toggleVideo,
    leaveRoom,
    retryConnection,
  };
}
